import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Undo2, CheckCircle2, ChevronDown, Download, Loader2, MinusCircle, Rocket, RefreshCw, Send, Truck, X, XCircle, Zap } from "lucide-react";
import { useMemo, useState } from "react";
import { useTenant } from "@/components/AppShell";
import { Button, cx, Input } from "@/components/ui";
import { api, ApiError, type OrderStatus } from "@/lib/api";
import { msg, t, tn } from "@/lib/i18n";

import { serverText } from "@/lib/server-text";
/** noch nicht beim Lager – nur dann lassen sich Aufträge ändern */
const OPEN: OrderStatus[] = ["received", "ready", "on_hold", "failed"];
type ActionKey = "rerequest" | "reprocess" | "backorder" | "force" | "express_on" | "express_off" | "method" | "cancel" | "cancel_return";
interface ActionDef {
  key: ActionKey;
  label: string;
  icon: typeof RefreshCw;
  /** für welche Status angeboten (Rest wird übersprungen) */
  statuses: OrderStatus[];
  manage?: boolean;
  danger?: boolean;
  hint: string;
}
const ACTIONS: ActionDef[] = [
  { key: "rerequest", label: msg("Fulfillment erneut bei Logiful anfragen"), icon: Send, statuses: ["cancelled"], hint: msg("Stornierte Aufträge laufen wieder durch alle Regeln (im Lager mit neuer Nummer „-2“) – fehlt Bestand, gehen sie nach dem Wareneingang automatisch ans Lager.") },
  { key: "reprocess", label: msg("Erneut prüfen"), icon: RefreshCw, statuses: ["on_hold", "failed"], hint: msg("Regeln und Daten neu prüfen, bei Erfolg direkt ans Lager.") },
  { key: "backorder", label: msg("Trotz Fehlbestand übertragen"), icon: Send, statuses: ["on_hold"], hint: msg("Nur Aufträge, die wegen Fehlbestand warten – Logiful versendet nach Wareneingang.") },
  { key: "force", label: msg("Übertragung erzwingen"), icon: Rocket, statuses: ["on_hold", "failed"], manage: true, hint: msg("Alle Gründe zum Zurückhalten werden übergangen.") },
  { key: "express_on", label: msg("Express (Priorität) setzen"), icon: Zap, statuses: OPEN, hint: msg("Nur Aufträge, die noch nicht beim Lager sind.") },
  { key: "express_off", label: msg("Express zurücknehmen"), icon: Zap, statuses: OPEN, hint: msg("Nur Aufträge, die noch nicht beim Lager sind.") },
  { key: "method", label: msg("Versandart ändern"), icon: Truck, statuses: OPEN, hint: msg("Nur Aufträge, die noch nicht beim Lager sind.") },
  { key: "cancel", label: msg("Bei Logiful stornieren"), icon: Ban, statuses: [...OPEN, "submitted", "in_progress"], danger: true, hint: msg("Nur bei Logiful (Lager und Connect), live geprüft – geht bis zur Kommissionierung. Im Shop bleibt die Bestellung unverändert.") },
  { key: "cancel_return", label: msg("Bei Logiful stornieren und an Shop zurückgeben"), icon: Undo2, statuses: [...OPEN, "submitted", "in_progress"], danger: true, hint: msg("Wie „Bei Logiful stornieren“ – zusätzlich gibt Connect Shopify-Aufträge an den Shop zurück („nicht ausgeführt“): ihr könnt dort selbst versenden oder erneut bei Logiful anfordern.") },
];
type Result = { id: string; label: string; outcome: "done" | "skipped" | "error"; message?: string };

/**
 * Sammelaktionen auf Auftragsebene. Jede Aktion läuft je Auftrag über dieselbe Funktion wie einzeln – gleiche Prüfungen,
 * Rechte und Verlaufseinträge. Ergebnis je Auftrag; nicht passende werden mit Grund übersprungen.
 */
export function OrderBulkBar({ ids, statusOf, labelOf, mayEdit, mayManage, onClear, onExport, exporting }: {
  ids: string[];
  statusOf: (id: string) => OrderStatus | undefined;
  labelOf: (id: string) => string;
  mayEdit: boolean;
  mayManage: boolean;
  onClear: () => void;
  onExport: () => void;
  exporting: boolean;
}) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [action, setAction] = useState<ActionDef | null>(null);
  const [methodId, setMethodId] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<Result[] | null>(null);
  const methods = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), staleTime: 10 * 60_000, enabled: action?.key === "method" });
  const methodList = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of methods.data?.warehouses ?? []) if (w.active || w.primary) for (const x of w.shippingMethods) m.set(x.id, x.name);
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [methods.data]);

  const eligible = (a: ActionDef) => ids.filter((id) => { const s = statusOf(id); return !s || a.statuses.includes(s); });

  const call = (a: ActionKey, id: string) => {
    switch (a) {
      case "rerequest": return api.orders.rerequest(tenantId, id);
      case "reprocess": return api.orders.reprocess(tenantId, id);
      case "backorder": return api.orders.backorderRelease(tenantId, id);
      case "force": return api.orders.force(tenantId, id);
      case "express_on": return api.orders.update(tenantId, id, { express: true });
      case "express_off": return api.orders.update(tenantId, id, { express: false });
      case "method": return api.orders.update(tenantId, id, { shippingMethodId: methodId || null });
      case "cancel": return api.orders.cancel(tenantId, id, "logiful");
      case "cancel_return": return api.orders.cancel(tenantId, id, "return_to_shop");
    }
  };

  const run = async (a: ActionDef) => {
    const todo = eligible(a);
    const skipped: Result[] = ids.filter((id) => !todo.includes(id)).map((id) => ({ id, label: labelOf(id), outcome: "skipped", message: t("passt nicht zum Status") }));
    const out: Result[] = [];
    setRunning({ done: 0, total: todo.length });
    // je Auftrag einzeln, drei gleichzeitig – fällt einer aus, laufen die übrigen weiter
    let next = 0;
    const worker = async () => {
      while (next < todo.length) {
        const id = todo[next++]!;
        try {
          await call(a.key, id);
          out.push({ id, label: labelOf(id), outcome: "done" });
        } catch (e) {
          const msg = e instanceof ApiError ? ((e.body as { message?: string })?.message ?? `HTTP ${e.status}`) : (e as Error).message;
          out.push({ id, label: labelOf(id), outcome: e instanceof ApiError && [409, 422].includes(e.status) ? "skipped" : "error", message: msg });
        }
        setRunning({ done: out.length, total: todo.length });
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setRunning(null);
    setAction(null);
    setConfirmText("");
    setResults([...out, ...skipped]);
    void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
  };

  const available = ACTIONS.filter((a) => (a.manage ? mayManage : mayEdit));
  const done = results?.filter((r) => r.outcome === "done").length ?? 0;
  const needsTyping = action?.danger;
  const count = action ? eligible(action).length : 0;

  return (
    <>
      <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-ink/20 bg-surface px-3 py-2 shadow-lg animate-fade-in">
        <span className="text-[13px] font-medium tabular-nums">{tn(ids.length, "{n} Auftrag ausgewählt", "{n} Aufträge ausgewählt")}</span>
        <button onClick={onClear} className="text-[12.5px] text-fg-muted hover:text-fg underline-offset-2 hover:underline">{t("Auswahl aufheben")}</button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button size="sm" icon={exporting ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />} disabled={exporting} onClick={onExport}>{t("CSV-Export")}</Button>
          {available.length > 0 && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button size="sm" variant="primary">{t("Aktion")} <ChevronDown className="size-3.5" /></Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={6} className="z-50 w-72 rounded-xl border border-border bg-surface p-1 shadow-xl animate-pop-in">
                  {available.map((a) => {
                    const n = eligible(a).length;
                    return (
                      <DropdownMenu.Item key={a.key} disabled={!n} onSelect={() => { setAction(a); setConfirmText(""); }} className={cx("flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2 data-[disabled]:opacity-40 data-[disabled]:cursor-default", a.danger && "text-danger")}>
                        <a.icon className="size-4 mt-0.5 shrink-0" />
                        <span className="min-w-0 flex-1"><span className="block font-medium">{t(a.label)}</span><span className="block text-[11.5px] text-fg-subtle">{tn(n, "betrifft {n} Auftrag", "betrifft {n} Aufträge")}</span></span>
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      </div>

      {/* Bestätigung */}
      <Dialog.Root open={!!action} onOpenChange={(o) => { if (!o && !running) setAction(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 animate-fade-in" />
          <Dialog.Content className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(480px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
            {action && (
              <>
                <Dialog.Title className={cx("text-[17px] font-semibold flex items-center gap-2", action.danger && "text-danger")}><action.icon className="size-4" /> {t(action.label)}</Dialog.Title>
                <Dialog.Description className="mt-1 text-[13px] text-fg-muted">
                  {tn(count, "Betrifft {n} Auftrag.", "Betrifft {n} Aufträge.")} {ids.length - count > 0 && tn(ids.length - count, "{n} passt nicht zum Status und wird übersprungen.", "{n} passen nicht zum Status und werden übersprungen.")} {t(action.hint)}
                </Dialog.Description>
                {action.key === "method" && (
                  <select value={methodId} onChange={(e) => setMethodId(e.target.value)} aria-label={t("Versandart")} className="mt-4 h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
                    <option value="">{t("Versandregeln / Standard des Lagers")}</option>
                    {methodList.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                )}
                {needsTyping && (
                  <label className="mt-4 block text-[13px]">
                    {t("Zur Bestätigung die Anzahl eintippen:")} <b className="tabular-nums">{count}</b>
                    <Input className="mt-1.5" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} inputMode="numeric" autoFocus />
                  </label>
                )}
                {running && (
                  <div className="mt-4">
                    <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden"><div className="h-full bg-ink transition-all" style={{ width: `${Math.round((running.done / Math.max(1, running.total)) * 100)}%` }} /></div>
                    <p className="mt-1.5 text-[12px] text-fg-muted tabular-nums">{running.done} / {running.total} – {t("bitte diese Seite geöffnet lassen")}</p>
                  </div>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <Button variant="ghost" disabled={!!running} onClick={() => setAction(null)}>{t("Abbrechen")}</Button>
                  <Button variant={action.danger ? "danger" : "primary"} loading={!!running} disabled={!count || (needsTyping && confirmText.trim() !== String(count))} onClick={() => void run(action)}>
                    {tn(count, "{n} Auftrag ausführen", "{n} Aufträge ausführen")}
                  </Button>
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Ergebnis je Auftrag */}
      <Dialog.Root open={!!results} onOpenChange={(o) => !o && setResults(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 animate-fade-in" />
          <Dialog.Content className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(560px,calc(100vw-32px))] max-h-[80vh] flex flex-col rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
            <Dialog.Title className="text-[17px] font-semibold">{t("Ergebnis")}</Dialog.Title>
            <Dialog.Description className="mt-1 text-[13px] text-fg-muted">
              {tn(done, "{n} erledigt", "{n} erledigt")} · {tn(results?.filter((r) => r.outcome === "skipped").length ?? 0, "{n} übersprungen", "{n} übersprungen")} · {tn(results?.filter((r) => r.outcome === "error").length ?? 0, "{n} Fehler", "{n} Fehler")}
            </Dialog.Description>
            <ul className="mt-3 overflow-y-auto divide-y divide-border text-[13px]">
              {[...(results ?? [])].sort((a, b) => ["error", "skipped", "done"].indexOf(a.outcome) - ["error", "skipped", "done"].indexOf(b.outcome)).map((r) => (
                <li key={r.id} className="py-1.5 flex items-start gap-2">
                  {r.outcome === "done" ? <CheckCircle2 className="size-4 mt-0.5 text-success shrink-0" /> : r.outcome === "skipped" ? <MinusCircle className="size-4 mt-0.5 text-fg-subtle shrink-0" /> : <XCircle className="size-4 mt-0.5 text-danger shrink-0" />}
                  <span className="min-w-0"><span className="font-medium tabular-nums">{t(r.label)}</span>{r.message && <span className="block text-[12px] text-fg-muted">{serverText(r.message)}</span>}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end"><Button onClick={() => setResults(null)} icon={<X className="size-3.5" />}>{t("Schließen")}</Button></div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
